import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { formatAgo, formatDateTime } from '../lib/format'
import type { Agent, Customer, EnrollmentTokenCreated, Site } from '../lib/types'
import { AgentStatusBadge } from '../components/Status'
import { CopyField } from '../components/CopyField'
import { KonfigurationsHinweis } from '../components/KonfigurationsHinweis'
import { EmptyState, ErrorBox, Loading } from '../components/Feedback'
import { CredentialSection } from '../components/CredentialForm'
import { AgentEmpfang, DatenverkehrAufbewahrung } from '../components/AgentEmpfang'
import {
  AgentAktualisieren,
  AgentSelbstaktualisierung,
  updateHinweis,
  useAgentUpdateStand,
} from '../components/AgentUpdate'
import { useAuth } from '../lib/auth'
import { Bandbreitentest } from '../components/Bandbreitentest'

interface Props {
  customerSlug: string | null
}

/**
 * Standorte und Agents. Hier entsteht ein Kunde, ein Standort und das
 * Einmal-Token samt Installationsbefehl für den Agent.
 */
export function StandortePage({ customerSlug }: Props) {
  const queryClient = useQueryClient()
  const { can } = useAuth()
  const [newToken, setNewToken] = useState<EnrollmentTokenCreated | null>(null)

  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: ({ signal }) => apiGet<Customer[]>('/customers', signal),
  })
  const customer = customerSlug
    ? customers.data?.find((c) => c.slug === customerSlug)
    : undefined

  const sites = useQuery({
    queryKey: ['sites', customer?.id ?? null],
    queryFn: ({ signal }) =>
      apiGet<Site[]>(customer ? `/sites?kunde=${customer.id}` : '/sites', signal),
    enabled: !customerSlug || Boolean(customer),
  })

  const agents = useQuery({
    queryKey: ['agents'],
    queryFn: ({ signal }) => apiGet<Agent[]>('/agents', signal),
    refetchInterval: 15_000,
  })

  const updateStand = useAgentUpdateStand()

  const createToken = useMutation({
    mutationFn: (vars: { siteId: string; agentName: string }) =>
      apiSend<EnrollmentTokenCreated>('POST', `/sites/${vars.siteId}/enrollment-tokens`, {
        agent_name: vars.agentName,
      }),
    onSuccess: (token) => setNewToken(token),
  })

  const revokeAgent = useMutation({
    mutationFn: (agentId: string) => apiSend<void>('POST', `/agents/${agentId}/revoke`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agents'] }),
  })

  if (customers.isError) return <ErrorBox error={customers.error} what="Kunden" />
  if (sites.isError) return <ErrorBox error={sites.error} what="Standorte" />

  const visibleAgents = customer
    ? (agents.data ?? []).filter((a) => sites.data?.some((s) => s.id === a.site_id))
    : (agents.data ?? [])

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Standorte &amp; Agents</h1>
          <div className="page-sub">
            {customer ? customer.name : 'Alle Kunden'} · {sites.data?.length ?? 0} Standorte ·{' '}
            {visibleAgents.length} Agents
          </div>
        </div>
      </div>

      <NewCustomerForm
        onCreated={() => queryClient.invalidateQueries({ queryKey: ['customers'] })}
      />

      <NewSiteForm
        customers={customers.data ?? []}
        preselected={customer?.id}
        onCreated={() => queryClient.invalidateQueries({ queryKey: ['sites'] })}
      />

      <CredentialSection customers={customers.data ?? []} />

      {customer && can('settings.manage') ? (
        <section className="card">
          <div className="card-head">
            <h2 className="card-title">Datenverkehr</h2>
            <span className="muted" style={{ fontSize: 12 }}>
              NetFlow, IPFIX und sFlow der Geräte
            </span>
          </div>
          <DatenverkehrAufbewahrung key={customer.id} customer={customer} />
        </section>
      ) : null}

      {newToken ? (
        <TokenPanel token={newToken} onClose={() => setNewToken(null)} />
      ) : null}

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Standorte</h2>
        </div>
        {sites.isPending ? (
          <Loading what="Standorte" />
        ) : (sites.data ?? []).length === 0 ? (
          <EmptyState
            title="Noch kein Standort"
            hint="Erst einen Kunden anlegen, dann einen Standort – danach lässt sich ein Agent registrieren."
          />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Standort</th>
                <th>Kunde</th>
                <th>Adresse</th>
                <th>Agents</th>
                <th>Geräte</th>
                <th>Agent registrieren</th>
              </tr>
            </thead>
            <tbody>
              {(sites.data ?? []).map((site) => (
                <tr key={site.id}>
                  <td>{site.name}</td>
                  <td className="muted">{site.customer_name}</td>
                  <td className="muted">{site.address || '–'}</td>
                  <td className="mono">{site.agent_count}</td>
                  <td className="mono">{site.device_count}</td>
                  <td>
                    <TokenButton
                      siteName={site.name}
                      pending={createToken.isPending}
                      onCreate={(agentName) =>
                        createToken.mutate({ siteId: site.id, agentName })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {createToken.isError ? (
          <div className="card-body" style={{ color: 'var(--crit)' }}>
            Token konnte nicht erzeugt werden: {(createToken.error as Error).message}
          </div>
        ) : null}
      </section>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Agents</h2>
          <span className="muted" style={{ fontSize: 12 }}>
            Aktualisiert sich alle 15 s
          </span>
        </div>
        <AgentSelbstaktualisierung kundeId={customer?.id} editable={can('agent.manage')} />
        {visibleAgents.filter(hatKonfigurationsBefund).map((agent) => (
          <KonfigurationsHinweis
            key={`konfiguration-${agent.id}`}
            lage={{
              fehler: agent.config_error,
              ausgelassen: ausgelasseneTeile(agent),
              agentName: agent.name,
              agentVersion: agent.version,
              befehl: agent.update_command,
            }}
          />
        ))}
        {agents.isPending ? (
          <Loading what="Agents" />
        ) : visibleAgents.length === 0 ? (
          <EmptyState
            title="Noch kein Agent registriert"
            hint="Token erzeugen und den angezeigten Befehl auf dem Kundenserver ausführen."
          />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Standort</th>
                <th>Status</th>
                <th>Version</th>
                <th>Zuletzt gesehen</th>
                <th>Geräte</th>
                <th>Zertifikat gültig bis</th>
                <th>Empfang</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibleAgents.map((agent) => (
                <tr key={agent.id}>
                  <td>
                    <div>{agent.name}</div>
                    <div className="muted mono" style={{ fontSize: 12 }}>
                      {agent.hostname || '–'}
                    </div>
                  </td>
                  <td className="muted">
                    {agent.customer_name} · {agent.site_name}
                  </td>
                  <td>
                    <AgentStatusBadge status={agent.status} connected={agent.connected} />
                    <StandbyZeile agent={agent} editable={can('agent.manage') && agent.status !== 'revoked'} />
                  </td>
                  <td className="mono">
                    {agent.version || '–'}
                    {hatKonfigurationsBefund(agent) ? (
                      <div style={{ color: agent.config_error ? 'var(--crit)' : 'var(--warn)', fontSize: 12 }}>
                        {agent.config_error ? 'Konfiguration abgelehnt' : 'Teile ausgelassen'}
                      </div>
                    ) : null}
                    {updateHinweis(agent) ? (
                      <div
                        style={{
                          color: updateHinweis(agent)?.fehler ? 'var(--crit)' : 'var(--muted)',
                          fontSize: 12,
                          fontFamily: 'inherit',
                        }}
                      >
                        {updateHinweis(agent)?.text}
                      </div>
                    ) : null}
                  </td>
                  <td className="muted">{formatAgo(agent.last_seen_at)}</td>
                  <td className="mono">{agent.device_count}</td>
                  <td className="muted mono">{formatDateTime(agent.cert_not_after)}</td>
                  <td>
                    <AgentEmpfang agent={agent} editable={can('agent.manage') && agent.status !== 'revoked'} />
                  </td>
                  <td className="row" style={{ gap: 8 }}>
                    {can('agent.manage') ? <AgentAktualisieren agent={agent} stand={updateStand.data} /> : null}
                    {agent.status === 'revoked' ? (
                      <span className="muted">gesperrt</span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={revokeAgent.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Zertifikat von ${agent.name} sperren? Der Agent wird sofort getrennt und muss neu registriert werden.`,
                            )
                          ) {
                            revokeAgent.mutate(agent.id)
                          }
                        }}
                      >
                        Sperren
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <Bandbreitentest agents={visibleAgents} editable={can('agent.manage')} />
    </>
  )
}

function TokenButton({
  siteName,
  pending,
  onCreate,
}: {
  siteName: string
  pending: boolean
  onCreate: (agentName: string) => void
}) {
  const [name, setName] = useState('')
  return (
    <div className="row" style={{ gap: 6 }}>
      <label className="sr-only" htmlFor={`agentname-${siteName}`}>
        Name des Agents für {siteName}
      </label>
      <input
        id={`agentname-${siteName}`}
        className="input"
        style={{ width: 180, height: 28, fontSize: 13 }}
        placeholder="agent-01"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <button
        type="button"
        className="btn btn-sm btn-primary"
        disabled={pending || name.trim() === ''}
        onClick={() => onCreate(name.trim())}
      >
        Token erzeugen
      </button>
    </div>
  )
}

function TokenPanel({ token, onClose }: { token: EnrollmentTokenCreated; onClose: () => void }) {
  return (
    <section className="card" style={{ borderColor: 'var(--acc)' }}>
      <div className="card-head">
        <h2 className="card-title">Token für {token.agent_name}</h2>
        <button type="button" className="btn btn-sm" onClick={onClose}>
          Schließen
        </button>
      </div>
      <div className="card-body stack">
        <p className="muted" style={{ margin: 0 }}>
          Das Token ist einmal verwendbar und läuft am {formatDateTime(token.expires_at)} ab. Es
          wird nur jetzt angezeigt und danach nie wieder. Der Installationsbefehl fragt es beim
          Ausführen ab – so steht es weder im Befehl noch in der Shell-History des Kundenservers.
        </p>
        <CopyField label="Installationsbefehl für den Kundenserver" value={token.install_command} />
        {token.windows_command ? (
          <CopyField label="Unter Windows: in einer PowerShell als Administrator" value={token.windows_command} />
        ) : null}
        <CopyField label="Token (bei der Abfrage einfügen)" value={token.token} />
      </div>
    </section>
  )
}

function NewCustomerForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('')
  const create = useMutation({
    mutationFn: () => apiSend<Customer>('POST', '/customers', { name: name.trim() }),
    onSuccess: () => {
      setName('')
      onCreated()
    },
  })

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Kunde anlegen</h2>
      </div>
      <form
        className="card-body row"
        style={{ gap: 12, alignItems: 'flex-end' }}
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim()) create.mutate()
        }}
      >
        <div className="field" style={{ maxWidth: 320, flexGrow: 1 }}>
          <label htmlFor="kundenname">Name</label>
          <input
            id="kundenname"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Muster GmbH"
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={create.isPending || !name.trim()}>
          Kunde hinzufügen
        </button>
        {create.isError ? (
          <span style={{ color: 'var(--crit)' }}>{(create.error as Error).message}</span>
        ) : null}
      </form>
    </section>
  )
}

function NewSiteForm({
  customers,
  preselected,
  onCreated,
}: {
  customers: Customer[]
  preselected?: string
  onCreated: () => void
}) {
  const [customerId, setCustomerId] = useState(preselected ?? '')
  const [name, setName] = useState('')
  const [address, setAddress] = useState('')

  const create = useMutation({
    mutationFn: () =>
      apiSend<Site>('POST', '/sites', {
        customer_id: customerId,
        name: name.trim(),
        address: address.trim(),
      }),
    onSuccess: () => {
      setName('')
      setAddress('')
      onCreated()
    },
  })

  const effectiveCustomer = customerId || preselected || ''

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Standort anlegen</h2>
      </div>
      <form
        className="card-body row"
        style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}
        onSubmit={(e) => {
          e.preventDefault()
          if (effectiveCustomer && name.trim()) create.mutate()
        }}
      >
        <div className="field" style={{ minWidth: 220 }}>
          <label htmlFor="standort-kunde">Kunde</label>
          <select
            id="standort-kunde"
            className="input"
            value={effectiveCustomer}
            onChange={(e) => setCustomerId(e.target.value)}
          >
            <option value="">Bitte wählen</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ minWidth: 200 }}>
          <label htmlFor="standort-name">Name</label>
          <input
            id="standort-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Wien"
          />
        </div>
        <div className="field" style={{ minWidth: 240, flexGrow: 1 }}>
          <label htmlFor="standort-adresse">Adresse</label>
          <input
            id="standort-adresse"
            className="input"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Musterstraße 1, 1010 Wien"
          />
        </div>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={create.isPending || !effectiveCustomer || !name.trim()}
        >
          Standort hinzufügen
        </button>
        {create.isError ? (
          <span style={{ color: 'var(--crit)' }}>{(create.error as Error).message}</span>
        ) : null}
      </form>
    </section>
  )
}

/** Hat der Agent zu seiner letzten Konfiguration etwas zu melden? */
export function hatKonfigurationsBefund(agent: Agent): boolean {
  return Boolean(agent.config_error) || (agent.config_dropped?.length ?? 0) > 0
}

/**
 * Was der Agent ausgelassen hat, in einer Zeile je Teil: Template und Metrik
 * beziehungsweise Nachbartabelle, dazu der Grund.
 */
export function ausgelasseneTeile(agent: Agent): string[] {
  const out: string[] = []
  for (const t of agent.config_dropped ?? []) {
    for (const teil of t.parts ?? []) {
      const was = teil.metric ?? teil.inventory ?? 'Template'
      out.push(`${t.template_key} · ${was}: ${teil.reason}`)
    }
    if ((t.parts?.length ?? 0) === 0) {
      out.push(t.template_key)
    }
  }
  return out
}

/**
 * Standby-Agent (Stufe 4.2): ein zweiter Agent am Standort, der die Geräte
 * übernimmt, wenn der erste ausfällt.
 */
export function StandbyZeile({ agent, editable }: { agent: Agent; editable: boolean }) {
  const queryClient = useQueryClient()
  const setzen = useMutation({
    mutationFn: (standby: boolean) => apiSend<{ standby: boolean }>('POST', `/agents/${agent.id}/standby`, { standby }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agents'] }),
  })
  const vertritt = agent.vertritt ?? 0
  const vertreten = agent.vertreten ?? 0
  return (
    <div className="stack" style={{ gap: 2, fontSize: 12, marginTop: 4 }}>
      {agent.is_standby ? <span className="muted">Standby für den Standort</span> : null}
      {vertritt > 0 ? (
        <span style={{ color: 'var(--warn)' }}>
          vertritt einen ausgefallenen Agent ({vertritt} {vertritt === 1 ? 'Gerät' : 'Geräte'})
        </span>
      ) : null}
      {vertreten > 0 ? (
        <span style={{ color: 'var(--warn)' }}>
          {vertreten} {vertreten === 1 ? 'Gerät fragt' : 'Geräte fragt'} der Standby ab
        </span>
      ) : null}
      {editable && vertritt === 0 ? (
        <button
          type="button"
          className="btn btn-sm"
          style={{ alignSelf: 'flex-start' }}
          disabled={setzen.isPending}
          onClick={() => setzen.mutate(!agent.is_standby)}
        >
          {agent.is_standby ? 'Nicht mehr Standby' : 'Als Standby nutzen'}
        </button>
      ) : null}
      {setzen.isError ? <span style={{ color: 'var(--crit)' }}>{(setzen.error as Error).message}</span> : null}
    </div>
  )
}
